import React, { useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  HStack,
  useColorModeValue,
  Image,
  IconButton,
  useDisclosure,
  Link,
} from "@chakra-ui/react";
import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";
import { useClerk } from "@clerk/clerk-react";
import { useUserAuthorization } from "../../context/UserRoleAndAuthContext";
import { useUserContext } from "../../context/userSignedInContext";
import { HashLink as LinkHash } from 'react-router-hash-link';
import { ReactComponent as AccessibilityIcon } from "../../assets/accessibility.svg"; 
import "./navbar.css";

function Navbar() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isSignedIn } = useUserContext();
  const bg = useColorModeValue("#b5b4b3");
  const { signOut } = useClerk();
  const { authorization, role  } = useUserAuthorization();

  const handleLogOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout failed ', error);
    }
  };

  const scrollWithOffset = (el, offset) => {
    const elementPosition = el.offsetTop - offset;
    window.scroll({
      top: elementPosition,
      left: 0,
      behavior: "smooth"
    });    
  };

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("menu-open");
    } else {
      document.body.classList.remove("menu-open");
    }
    return () => document.body.classList.remove("menu-open");
  }, [isOpen]);

  return (
    <Box
      as="nav"
      position="fixed"
      top={0}
      width="100%"
      height= {{base: '4rem',sm: '3.8rem' ,md: '4rem', lg: '4.4rem', xl: '4.6rem'}} 
      bg={bg}
      zIndex={1000}
      dir="ltr"
    >
      <Flex
        h={{base: '4rem', sm: '3rem', md: '3.4rem',lg:"4rem", xl: '4.3rem'}}
        alignItems={"center"}
        justifyContent={"space-between"}
        px={4}
      >
        <Flex alignItems={"center"}>
          <Link href="/" aria-label="homepage">
            <Image
              src="https://leadguitar.s3.eu-north-1.amazonaws.com/images/logobw.png"
              alt="leadguitar black and white"
              className="logo-image"
              width={{ base: "18rem", sm: "15rem", md: '8rem'}}
              height={{ base: "7rem" ,md: '5rem', lg: '7rem'}}
              role="button"
              tabIndex="0"
            />
          </Link>
          <Link href="/accessibility" aria-label="Accessibility" alt="">
            <AccessibilityIcon width="3.2rem" height='2rem' alt="accessibility" />
          </Link>
        </Flex>
        <Flex
          alignItems={"center"}
          maxW="1200px"
          w="100%"
          justifyContent={"flex-end"}
        >
          <IconButton
            size={"md"}
            icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
            aria-label={"Open Menu"}
            display={{ md: "none" }}
            onClick={isOpen ? onClose : onOpen}
          />
          <HStack as={"nav"} spacing={8} display={{ base: "none", md: "flex" }}>
            {isSignedIn ? (
              <Box display="flex">
                {role!=='visitor' &&(
                  <Link href="/dashboard" className="nav-link">
                    <Text fontSize="3xl" role="button" as="h3" tabIndex="0">קורס דיגיטלי</Text>
                  </Link>
                )}
                {role==="premium" && (
                  <Link href="/schedule" className="nav-link" ml={6}>
                    <Text fontSize="3xl" role="button" as="h3">קביעת שיעור</Text>
                  </Link>
                )}
                {authorization==="admin" && (
                  <Link href="/admin" className="nav-link" ml={6}>
                    <Text fontSize="3xl" role="button" as="h3">ניהול</Text>
                  </Link>
                )}
                <Link
                  href="/"
                  className="nav-link"
                  onClick={handleLogOut}
                  ml={6}
                >
                  <Text fontSize="3xl" role="button" as="h3" tabIndex="0">התנתקות</Text>
                </Link>
              </Box>
            ) : (
              <>
                <LinkHash to="/#intro" smooth scroll={el => scrollWithOffset(el, -520)} className="nav-link">
                  <Text role="button" fontSize={{base: '1rem', md: '1rem', lg: '1.5rem'}} as="h3" tabIndex="0">צור קשר</Text>
                </LinkHash>
                <Link href="/recommendations" className="nav-link">
                  <Text fontSize={{base: '1rem', md: '1rem', lg: '1.5rem'}} role="button" as="h3" tabIndex="0">תלמידים ממליצים</Text>
                </Link>
                <Link href="/teachers" className="nav-link">
                  <Text fontSize={{base: '1rem', md: '1rem', lg: '1.5rem'}} role="button" as="h3" tabIndex="0">המורים שלנו</Text>
                </Link>
                <LinkHash to="/syllabus" smooth scroll={el => scrollWithOffset(el, -520)} className="nav-link">
                  <Text role="button" fontSize={{base: '1rem', md: '1rem', lg: '1.5rem'}} as="h3" tabIndex="0">סילבוס</Text>
                </LinkHash>
                <Link href="/sign-in" className="nav-link">
                  <Text fontSize={{base: '1rem', md: '1rem', lg: '1.5rem'}} role="button" as="h3" tabIndex="0">התחברות</Text>
                </Link>
              </>
            )}
          </HStack>
        </Flex>
      </Flex>
      {isOpen && (
        <Box pb={4} display={{ md: "none" }} bg={bg}>
          <Flex direction="column" align="center">
            {isSignedIn ? (
              <>
                {role!=='visitor' &&(
                  <Link href="/dashboard" className="nav-link">
                    <Text fontSize="3xl" role="button" as="h3" tabIndex="0">קורס דיגיטלי</Text>
                  </Link>
                )}
                {role==="premium" && (
                  <Link href="/schedule" className="nav-link" ml={6}>
                    <Text fontSize="3xl" role="button" as="h3">קביעת שיעור</Text>
                  </Link>
                )}
                {authorization==="admin" && (
                  <Link href="/admin" className="nav-link" ml={6}>
                    <Text fontSize="3xl" role="button" as="h3">ניהול</Text>
                  </Link>
                )}
                <Link href="/" className="nav-link" onClick={handleLogOut}>
                  <Text fontSize="3xl" role="button" as="h3" tabIndex="0">התנתקות</Text>
                </Link>
              </>
            ) : (
              <>
                <Link href="/sign-in" className="nav-link" mt={2}>
                  <Text fontSize="2xl" role="button" as="h3" tabIndex="0">התחברות</Text>
                </Link>
                <Link href="/recommendations" className="nav-link">
                  <Text fontSize="2xl" role="button" as="h3" tabIndex="0">תלמידים ממליצים</Text>
                </Link>
                <LinkHash to="/#LessonStructure" smooth scroll={el => scrollWithOffset(el, 60)}>
                  <Text role="button" fontSize="2xl" as="h3" tabIndex="0">שיטת לימוד</Text>
                </LinkHash>
                <Link href="/teachers" className="nav-link">
                  <Text fontSize="2xl" role="button" as="h3" tabIndex="0">המורים שלנו</Text>
                </Link>
                <LinkHash to="/#intro" smooth scroll={el => scrollWithOffset(el, -200)}>
                  <Text role="button" fontSize="2xl" as="h3" tabIndex="0">צור קשר</Text>
                </LinkHash>
              </>
            )}
          </Flex>
        </Box>
      )}
    </Box>
  );
}

export default Navbar;
